// Phones (VIS-230; the one column, the owner's pick "1A Capsule"): the top of the map stays empty and nothing is
// docked at the bottom at rest. ONE slim column in the bottom-right corner holds, top to bottom: Search (or the
// "Where to?" pin), a style's own buttons (`lead`), the settings rows, Settings, the map tools and the map data (i).
// Settings opens the rows upward, a word and the value beside each; a row's choices then take the rows' place (in a
// style's side mode: one row left of the column). A choice presses or drives the page's own control, which stays on
// the page hidden (`data-ph`), so presets, ghosts and the editor work as on a computer.
// Nothing here knows a style's markup: controls are found by their `data-control` or the selector of the settings list
// (editor.js controlSelector), so every style gets the same column and re-skins it with CSS (README "Phones").
// Phone = max-width 760 px, outside the lobby layouts and ?kiosk. Desktop never runs any of it.
// Written small on purpose: every guest downloads it (scripts/ci/check-assets.mjs, the engine budget). No loop: it
// measures when something changed (fit) and writes only what changed.
import { html } from '../dom.js';
import { controlSelector as sel } from './editor.js';
import { icon, hasIcon } from './icons.js';
import { SEASON_ICON } from './time.js';
const mask = (n) => html`<span class="ic ph-i" data-i="${n}"></span>`; // an icon of the column's own, drawn by CSS (style.css .ph-i)

/** B is on: a narrow window (max-width 760 px, ui.js), not a lobby layout (or one with a panel), not ?kiosk. */
export const phoneOn = (narrow, layout, kiosk) => !!narrow && !kiosk && !layout?.panel && !/^lobby/.test(layout?.id || '');
/** The map tools the column keeps under Settings, in this order (the map data (i) comes last, always). */
export const TOOLS = ['locate', 'compass', 'home'];
/** Not on a phone: an iPhone cannot go full screen; the northern lights are a switch there. (No style has zoom, quality, clouds or layers buttons: controls.js NO_BUTTON.) */
export const DROP = ['fullscreen', 'aurora.panel'];
const ids = (s) => s.split(' ');
/**
 * The settings rows, in order: `{ id, ctl, icon, word }` (`word`: the interface key `ph.row.<id>`). `ctl`: the controls a
 * row stands for; the row is there when one of them is drawn, and its choices are CHOICES[id] (Northern lights: none,
 * the row is a switch). The tour's icon is a route, never a pin (the owner: a pin does not say "tour").
 */
export const ROWS = 'tour,tour.picker tour.button,route|light,time.hour time.dates time.pickDate time.play,clock|season,season.switch,|aurora,aurora.toggle,aurora|trees,trees,tree|lang,language.switch,globe'.split('|')
  .map((r) => { const [id, ctl, icon] = r.split(','); return { id, ctl: ids(ctl), icon, word: `ph.row.${id}` }; });
/** Pure: the rows for what is on the page (`has(id)`), each with the controls of it that are drawn (`on`). */
export const railRows = (has, rows = ROWS) => rows.map((r) => ({ ...r, on: r.ctl.filter(has) })).filter((r) => r.on.length);
/** The rows that open a panel beside the column instead of choices (the owner, 2026-11-05: the real controls, not a
 *  short list): Light the time card (clock, the hour slider, play, the day and its dates), Trees the trees popover with
 *  its slider (column.js), Northern lights its own panel (auroraPanel.js) where the preset draws `aurora.panel`. */
export const POPS = ['light', 'trees', 'aurora'];

const txt = (el, s) => (s ? el.querySelector(s) : el)?.textContent.trim() || '';
const isOn = (el) => el.classList.contains('on');
/**
 * A row's choices, made from the page's own controls only (a control the preset hides is not drawn, so it offers
 * nothing): `[{ id, html, label, value?, on, press, ctl, kind?, cls? }]`. kind `hour`: the clock, its slider (only with
 * `time.hour`) and play (only with `time.play`); `dot`: a named date of the season on screen; `pick`: the date picker.
 * `x`: `{ has(id), q(selector): the page's elements (the phone's own left out), T, tx, num, tours, density(): 0..100,
 * amount(v), done(): closes the menu }`.
 */
export const CHOICES = {
  season: (x) => x.q('.season').map((b) => ({ id: b.dataset.season, html: icon(SEASON_ICON[b.dataset.season] || 'sun'), label: txt(b, 'span') || b.title, value: txt(b, '.soon'), on: isOn(b), press: () => b.click(), ctl: 'season.switch' })),
  light(x) {
    const play = (x.has('time.play') && x.q('#play')[0]) || null, go = play?.getAttribute('aria-pressed') === 'true', hour = x.has('time.hour');
    const day = (x.has('time.pickDate') && x.q('#date-custom')[0]) || null;
    return [
      (hour || play) && { id: 'hour', kind: 'hour', html: play ? icon(go ? 'pause' : 'play') : mask('clock'), label: x.T(go ? 'time.pause' : 'time.play'), on: go, press: play && (() => play.click()), ctl: hour ? 'time.hour' : 'time.play', slider: hour },
      ...(x.has('time.dates') ? x.q('.date[data-i]').filter((b) => !b.hidden).map((b) => ({ id: `d${b.dataset.i}`, kind: 'dot', label: txt(b, 'b'), value: txt(b, 'small'), on: isOn(b), press: () => b.click(), ctl: 'time.dates' })) : []),
      day && { id: 'pick', kind: 'pick', html: icon('calendar'), label: txt(day, 'b span'), value: isOn(day) ? txt(day, 'small') : '', on: isOn(day), ctl: 'time.pickDate' },
    ].filter(Boolean);
  },
  lang: (x) => x.q('.langs .lang').map((b) => ({ id: b.dataset.lang, html: html`<b class="ph-code">${b.dataset.lang.toUpperCase()}</b>`, label: b.title || b.dataset.lang, on: isOn(b), press: () => b.click(), ctl: 'language.switch' })),
  tour: (x) => [...(x.tours.current() ? [{ id: '', html: icon('close'), label: x.T('tour.stop'), press: () => x.tours.stop() }] : []),
    ...x.tours.list().filter((t) => t.stops > 0).map((t) => ({ id: t.id, html: icon('route'), label: x.tx(t.name), value: x.T('tour.stops', { n: x.num(t.stops) }), press: () => { x.done(); x.tours.start(t.id); } }))],
};

export function phoneParts(c) {
  const { api, T, i18n } = c, B = () => document.body, $ = (s, r = document) => r?.querySelector(s), $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const cls = (k, on) => k && B().classList.toggle(k, on);
  // what is open (it survives a redraw): Settings (`rail`), a row's choices (`row`) or its panel (`pop`, POPS; `days`:
  // the time card's dates), Search (`find`), a style's side row (`side`: { anchor, items }); `want`: the choice pressed
  // last, shown chosen until the page says so
  const S = { rail: false, row: null, pop: null, days: false, find: false, side: null, want: null };
  let rows = [], items = [], drawnM = '', drawnS = '', drawnC = '', shown = '', chose = '', railWas = false, bound, fitT, ro, tourMod, stop, sideMode = false, card = false, drag = false, leadEl;
  const st = () => api.getState(), drawn = (id) => c.drawn.has(id) || c.ghosts.has(id);
  const els = (id, x = c.reg.control(id)) => (x ? $$(sel(x)).filter((e) => !e.closest('#ph')) : []);
  const X = {
    has: drawn, q: (s) => $$(s).filter((e) => !e.closest('#ph')), T, tx: c.tx, num: i18n.num, tours: api.tours, done: () => set({}),
    density: () => (api.trees?.settings().density ?? 1) * 100,
  };
  // a row's panel: the page's own popover, opened and closed by its own (hidden) button, placed beside the column by CSS
  const isPop = (id) => POPS.includes(id) && (id !== 'aurora' || drawn('aurora.panel'));
  const popBtn = (id) => els(id === 'aurora' ? 'aurora.toggle' : id)[0];
  const popEl = (id) => { const b = id && id !== 'light' && popBtn(id); return (b && document.getElementById(b.getAttribute('aria-controls'))) || null; };
  function popOpen(id, on) {
    const b = id && id !== 'light' && popBtn(id);
    if (!b) return;
    if ((b.getAttribute('aria-expanded') === 'true') !== on) b.click(); // its own press: popup.js opens or closes it
    popEl(id)?.classList.toggle('ph-pop', on);
  }
  const lead = () => (leadEl ||= Object.assign(document.createElement('div'), { className: 'ph-lead', id: 'ph-lead' }));
  const openSide = (anchor, list) => set(anchor && list?.length ? { side: { anchor, items: list } } : {});
  const ph = {
    /** Styles may reorder or trim these before the first draw (README "Phones"). */
    rows: ROWS, tools: TOOLS,
    /** A slot at the top of the column, after Search, for a style's own buttons; the same element after every redraw. */
    get lead() { return lead(); },
    /** `ph.side = true` (a style, before the first draw): a row's choices open in one row left of the column, icons only.
     *  `ph.side(anchor, items)` opens such a row beside a style's own button (items as CHOICES give them); `ph.side(null)` closes it. */
    get side() { return openSide; },
    set side(v) { sideMode = !!v; },
    region: {
      key: () => c.phone,
      html: () => (c.phone ? html`<div id="ph"><nav id="ph-rail" class="ph-rail" aria-label="${T('ph.menu')}"><button class="ph-find" id="ph-find" data-a="find" aria-expanded="false" aria-label="${T('ph.search')}" hidden>${icon('search')}</button><div class="ph-rows" id="ph-rows" role="group" aria-label="${T('ph.settings')}"></div><button class="ph-set" id="ph-set" data-a="set" aria-expanded="false">${mask('sliders')}${icon('close', 'ph-x')}</button><span class="ph-dv"></span><div class="ph-tools"></div></nav><div class="ph-side" id="ph-side" role="group" hidden></div><div class="ph-pop ph-time" id="ph-time" role="group" aria-label="${T('ph.row.light')}" hidden></div></div>` : ''), // (the tour's caption #ph-tour comes with its own module, lazy/phtour.js)
      wire(root) {
        if (!root) return;
        drawnM = drawnS = drawnC = '';
        $('#ph-rows', root).before(lead());
        root.onclick = press;
        root.oninput = (e) => e.target.matches('.ph-hr') && api.scrubTime(+e.target.value); // as the time dock's own slider (time.js)
        root.onchange = (e) => {
          const t = e.target, p = $('#date-pick');
          if (t.matches('.ph-hr')) drag = false;
          else if (t.matches('.ph-date') && t.value && p) { p.value = t.value; p.dispatchEvent(new Event('change')); } // the page's own picker takes the day
        };
        root.onpointerdown = (e) => { if (!e.target.matches?.('.ph-hr')) return; drag = true; const p = $('#play'); if (p?.getAttribute('aria-pressed') === 'true') p.click(); };
      },
    },
  };

  // ---- presses inside the phone's own parts
  function press(e) {
    const b = e.target.closest('[data-a],[data-row],[data-k],#wayfind-btn'), a = b?.dataset.a, key = e.detail === 0;
    if (!b || e.target.matches('.ph-date')) return; // (the picker's own click, sent by its label)
    if (b.id === 'wayfind-btn') set({}); // its own press opens "Where to?" (lazy/whereto.js); what was open closes
    else if (a === 'set') set(S.rail ? {} : { rail: true }, key);
    else if (a === 'find') set({ find: !S.find });
    else if (a === 'back') set(S.row ? { rail: true } : {}, key);
    else if (a === 'days') { S.days = !S.days; paint(); }
    else if (b.dataset.row && isPop(b.dataset.row)) set({ rail: true, pop: S.pop === b.dataset.row ? null : b.dataset.row }, key);
    else if (b.dataset.row === 'aurora') { els('aurora.toggle')[0]?.click(); paint(); } // a switch: it changes in place
    else if (b.dataset.row) set({ rail: true, row: S.row === b.dataset.row ? null : b.dataset.row }, key);
    else choose(items[b.dataset.k], b, e);
  }
  function choose(it, b, e) {
    if (!it) return;
    if (it.kind === 'pick') { try { $('.ph-date', b).showPicker(); e.preventDefault(); } catch { /* errors:ignore: the label opens the picker itself */ } return; }
    if (S.row && it.kind !== 'hour') S.want = it.id;
    if (it.kind === 'dot') S.days = false; // a day picked: the card says it again
    it.press?.();
    paint();
  }
  /** Opens one thing (or nothing: `{}`); everything else closes. */
  function set(next, focus) {
    const was = S.pop;
    Object.assign(S, { rail: false, row: null, pop: null, find: false, side: null, want: null }, next);
    S.rail &&= rows.length > 0;
    if (!S.rail) S.row = S.pop = null;
    if (S.pop !== was) { S.days = false; popOpen(was, false); popOpen(S.pop, true); }
    if (c.phone) cls('menu-open', S.find); // the page's "the list is up" (explore.js, the styles) is Search's
    apply();
    if (focus) $(sideMode && S.row ? '#ph-side [data-k]' : '#ph-rows button')?.focus();
  }

  // ---- what the column shows
  const onOff = (v) => T(v ? 'ph.on' : 'ph.off');
  const VAL = {
    light: () => i18n.clock(st().hour), season: () => c.seasonName(c.season), aurora: () => onOff(st().auroraOn),
    trees: () => `${i18n.num(Math.round(X.density()))} %`, lang: () => T(`set.lang.current.${i18n.lang}`) || i18n.lang,
  };
  const rowIcon = (r) => (r.id === 'season' ? icon(SEASON_ICON[c.season] || 'sun') : hasIcon(r.icon) ? icon(r.icon) : mask(r.icon));
  // a control the editor shows as a ghost marks its row and choices too (ui.js dims them; VIS-73: still pressable)
  const at = (id) => (id ? html` data-control="${id}"${c.ghosts.has(id) ? ' data-ghost' : ''}` : '');
  const tag = (it) => (it.kind === 'pick' ? 'label' : 'button');
  const pickIn = (it) => (it.kind === 'pick' ? html`<input type="date" class="ph-date" tabindex="-1" aria-hidden="true">` : ''); // (time.js reads its month and day)
  // `--i`: the order they rise in, the nearest to Settings first
  const rowsHTML = () => html`${rows.map((r, i) => html`<button class="ph-row" data-row="${r.id}"${at(r.on[0])} style="--i:${rows.length - i}"><span class="ph-lb">${T(r.word)} <b data-v="${r.id}"></b></span><span class="ph-ic">${rowIcon(r)}</span></button>`)}`;
  function cell(it, k) {
    const a = html`${at(it.ctl)} style="--i:${items.length - k}"`;
    return html`<${tag(it)} class="ph-row ph-ch ph-${it.kind || 'one'} ${it.cls || ''}" data-k="${k}"${a}><span class="ph-lb">${it.label}${it.value ? html` <b>${it.value}</b>` : ''}</span><span class="ph-ic">${it.kind === 'dot' ? html`<i class="ph-d"></i>` : it.html}</span>${pickIn(it)}</${tag(it)}>`;
  }
  const backRow = (r) => html`<button class="ph-row ph-back" data-a="back" style="--i:0"><span class="ph-lb">${icon('prev')}${T(r.word)} <b data-v="${r.id}"></b></span><span class="ph-ic">${rowIcon(r)}</span></button>`;
  /** Side mode: the same cells in one row beside the button that opened it (CSS keeps their words for screen readers
   *  only), the close last. */
  const sideHTML = () => html`<div class="ph-side-row">${items.map(cell)}<button class="ph-ic ph-sback" data-a="back" aria-label="${T('card.close')}">${icon('close')}</button></div>`;
  /** The time card (Light): the clock and the phase with play beside them, the hour slider, then one row for the day
   *  ("21 Dec · Polar night", Change day) that opens the season's named dates and Pick a date in the card. Each part
   *  only where the preset draws its control (CHOICES.light). The owner, 2026-11-05: one calm card, not a menu of rows. */
  function timeHTML() {
    const k = (it) => items.indexOf(it), hour = items.find((it) => it.kind === 'hour'), dots = items.filter((it) => it.kind === 'dot'), pick = items.find((it) => it.kind === 'pick');
    const now = dots.find((it) => it.on) || (pick?.on ? pick : null);
    return html`<div class="ph-tc-head"><span class="ph-tc-now"><b class="ph-clock"></b><small class="ph-phase"></small></span>${hour?.press ? html`<button class="ph-tc-play" data-k="${k(hour)}" aria-label="${hour.label}">${hour.html}</button>` : ''}</div>
      ${hour?.slider ? html`<input class="ph-hr" type="range" min="0" max="24" step="0.05" aria-label="${T('time.hour')}">` : ''}
      ${dots.length || pick ? html`<button class="ph-tc-day" data-a="days" aria-expanded="${String(S.days)}">${icon('calendar')}<span class="ph-tc-txt"><b>${now?.label || ''}</b> <small>${now?.value || ''}</small></span><span class="ph-tc-go">${T('time.changeDay')}${icon(S.days ? 'fold' : 'next')}</span></button>` : ''}
      ${S.days ? html`<div class="ph-tc-days">${dots.map((it) => html`<button class="ph-tc-d" data-k="${k(it)}"${at(it.ctl)}><i class="ph-d"></i><span><b>${it.label}</b> <small>${it.value}</small></span></button>`)}${pick ? html`<label class="ph-tc-d ph-tc-pick" data-k="${k(pick)}"${at(pick.ctl)}>${icon('calendar')}<span><b>${pick.label}</b></span><input type="date" class="ph-date" tabindex="-1" aria-hidden="true"></label>` : ''}</div>` : ''}`;
  }
  /** Draws the rows or a row's choices (and the side row) when what they are changed; the values are live(). */
  function paint() {
    const box = $('#ph-rows'), side = $('#ph-side'), tc = $('#ph-time');
    if (!box) return;
    const r = S.row && rows.find((x) => x.id === S.row);
    if (!r) S.row = null; // (a row no longer drawn)
    if (S.pop && !rows.some((x) => x.id === S.pop)) S.pop = null;
    items = r ? CHOICES[r.id]?.(X) || [] : S.pop === 'light' ? CHOICES.light(X) : S.side?.items || [];
    const now = `${S.rail}${S.row}${S.pop}${!!S.side}`, still = now === shown, M = r && !sideMode ? html`${items.map(cell)}${backRow(r)}` : rowsHTML(), Z = html`${(r && sideMode) || S.side ? sideHTML() : ''}`;
    // a new thing opened rises from its button; the same one drawn again (a language, a date picked) stays still
    if (S.rail && !railWas) box.classList.remove('ph-still');
    if (`${M}` !== drawnM) { drawnM = `${M}`; box.classList.toggle('ph-still', still); box.innerHTML = html`${M}`; }
    if (`${Z}` !== drawnS) { drawnS = `${Z}`; side.classList.toggle('ph-still', still); side.innerHTML = html`${Z}`; }
    side.hidden = !drawnS;
    const C = S.pop === 'light' ? `${timeHTML()}` : '';
    if (C !== drawnC && !drag) { drawnC = C; tc.innerHTML = html`${timeHTML()}`; } // (not while the slider is held)
    tc.hidden = !C;
    shown = now; railWas = S.rail;
    live();
  }
  const setText = (el, s) => { if (el && el.textContent !== s) el.textContent = s; };
  /** The values now: each row's, the chosen choice, the clock. Writes only what changed (a time-lapse calls it a frame). */
  function live() {
    const root = $('#ph');
    if (!root) return;
    for (const b of $$('[data-v]', root)) setText(b, VAL[b.dataset.v]?.() || '');
    for (const b of $$('[data-row]', root)) {
      const v = String(S.row === b.dataset.row || S.pop === b.dataset.row); // (side mode: the row whose choices are open)
      b.classList.toggle('on', b.dataset.row === 'aurora' && !!st().auroraOn);
      if (b.getAttribute('aria-expanded') !== v) b.setAttribute('aria-expanded', v);
    }
    let ons = items.map((it) => !!(typeof it.on === 'function' ? it.on() : it.on));
    const w = items.findIndex((it) => it.id === S.want);
    if (w >= 0) { if (ons[w]) S.want = null; else ons = ons.map((v, k) => (items[k].kind === 'hour' ? v : k === w)); } // (a season takes a moment to switch)
    for (const el of $$('[data-k]', root)) { const v = String(!!ons[el.dataset.k]); el.classList.toggle('on', v === 'true'); if (el.getAttribute('aria-pressed') !== v) el.setAttribute('aria-pressed', v); }
    const h = st().hour;
    for (const p of $$('#ph-time', root)) {
      setText($('.ph-clock', p), i18n.clock(h)); setText($('.ph-phase', p), c.phase ? T(`phase.${c.phase}`) : '');
      const r = $('.ph-hr', p);
      if (r && !drag) r.value = h;
    }
  }

  // ---- the column: the tools, the "Where to?" pin and the credit moved in; what the column stands for hidden
  const hosted = () => new Set([...rows.flatMap((r) => r.ctl), ...DROP]);
  const inside = (root) => c.reg.controls.filter((x) => $(sel(x), root)).map((x) => x.id);
  function adopt() {
    // the first of a tool's elements in the page's order is the one its region drew last (the column comes after them)
    const rail = $('#ph-rail'), box = $('.ph-tools', rail), first = (s) => { const [e, ...old] = $$(s); for (const o of old) if (o.closest('#ph')) o.remove(); return e ? [e] : []; };
    for (const id of ph.tools) for (const e of first(sel(c.reg.control(id)))) drawn(id) ? box.append(e) : e.remove(); // the column's order
    box.append(...first('#credit-btn')); // the map data (i), last
    for (const e of first('#wayfind-btn')) drawn('wayfind') ? rail.prepend(e) : e.remove(); // "Where to?" tops the column in Search's place
    // a region whose every control is in the column goes whole, else each element (the rest keeps its own place)
    const H = hosted();
    for (const el of $$('#chrome [data-ph]')) delete el.dataset.ph;
    for (const el of $$('#chrome>:not(#ph)')) {
      const on = inside(el);
      if (on.length && on.every((x) => H.has(x))) el.dataset.ph = '';
      else for (const id of on) if (H.has(id)) for (const e of els(id)) if (e.closest('#chrome>*') === el) e.dataset.ph = '';
    }
  }

  // ---- the page's state as classes, and where things stand
  function apply() {
    const s = $('#ph-set');
    cls('ph-rail-open', S.rail);
    cls('ph-searching', S.find);
    cls(chose, false);
    chose = S.row && `ph-choose-${S.row}`;
    cls(chose, true);
    cls('ph-choose', !!S.row);
    cls('ph-side-open', !!(S.side || (S.row && sideMode)));
    if (s) { s.setAttribute('aria-expanded', S.rail); s.ariaLabel = s.title = T(S.rail ? 'ph.settings.close' : 'ph.settings'); }
    $('#ph-find')?.setAttribute('aria-expanded', S.find);
    paint();
    fit();
  }
  const fitSoon = () => { cancelAnimationFrame(fitT); fitT = requestAnimationFrame(fit); };
  const props = {};
  const prop = (k, px) => { const v = `${px | 0}px`; if (props[k] !== v) document.documentElement.style.setProperty(k, (props[k] = v)); };
  /** --ph-b: how much of the bottom an open part covers (the place card, the tour, a route, a style's bar: whatever
   *  spans most of the width at the bottom of the screen and reaches under the column's corner); the column stands
   *  above it (--ph-rb). --ph-room: the most a card may grow to, the closed column still above it. --ph-rest: the
   *  column without its rows. --ph-find-b / --ph-find-r: the bottom of Search (or the pin) and the column's left edge +
   *  10 px, where the list of places and "Where to?" stand; --ph-side-b: the bottom of the button a side row opened
   *  from. A column that would leave the top of the screen comes down: never off the screen (VIS-251). */
  function fit() {
    const H = innerHeight, W = innerWidth, rail = $('#ph-rail');
    if (!rail || !B().classList.contains('ph')) return;
    let cover = 0;
    for (const el of $$('#chrome>:not(#ph,#explore),#ph-tour,#feature-card.open,.loc-bar.route')) {
      ro?.observe(el); // a part that grows (a card's photos) moves the column too
      const r = el.getBoundingClientRect(), cs = getComputedStyle(el);
      if (r.width > W * 0.6 && r.right > W - 64 && r.bottom > H - 40 && cs.visibility !== 'hidden' && cs.opacity > 0.05) cover = Math.max(cover, H - r.top); // (display: none has no box)
    }
    prop('--ph-b', cover);
    ro?.observe(rail); // the rows opening, choices of another length
    rail.hidden = !rows.length && !$('.ph-tools>*,.ph-lead>*,#wayfind-btn,#ph-find:not([hidden])', rail);
    const h = rail.offsetHeight, rest = h - $('#ph-rows').offsetHeight, room = H - 56 - h, want = Math.max(28, cover + 14), top = H - 8 - h; // the CSS bottom is max(28px, --ph-b + 14px)
    prop('--ph-rest', rest);
    prop('--ph-room', H - 70 - (rail.hidden ? 0 : rest));
    rail.style.bottom = (S.rail || S.side) && want > room ? `${Math.max(8, room)}px` : want > top ? `${Math.max(8, top)}px` : ''; // (closed: down only as far as it must)
    const f = $('.ph-find:not([hidden])', rail)?.getBoundingClientRect(), a = S.side?.anchor || (sideMode && S.row && $(`[data-row="${S.row}"]`, rail));
    prop('--ph-find-r', W - rail.getBoundingClientRect().left + 10);
    if (f?.height) prop('--ph-find-b', H - f.bottom);
    if (a?.isConnected) prop('--ph-side-b', H - a.getBoundingClientRect().bottom);
    // a row's panel stands left of the column level with its row (side mode: above the row of icons); a popover that
    // closed by itself (Escape, a press outside it) closes the row's mark too
    const pa = S.pop && $(`[data-row="${S.pop}"]`, rail), pe = popEl(S.pop);
    if (pa) { const r = pa.getBoundingClientRect(); prop('--ph-pop-b', sideMode ? H - r.top + 8 : H - r.bottom); }
    if (pe) ro?.observe(pe);
    if (pe?.hidden) { S.pop = null; live(); }
  }

  // ---- the tour's caption: its own module (lazy/phtour.js), fetched when a tour first stops
  const drawTour = () => (stop || tourMod) && (tourMod ||= import('../lazy/phtour.js')).then((m) => { m.default(c, stop); fitSoon(); }, () => {}); // errors:ignore: no caption; the tour plays on

  function bind() {
    if (bound) return;
    bound = true;
    addEventListener('resize', fitSoon);
    ro = globalThis.ResizeObserver && new ResizeObserver(fitSoon);
    for (const k of ['transitionend', 'animationend']) document.addEventListener(k, fitSoon, true);
    // a place card that opens closes the menu; the list's own cross or a pick folds Search (body.menu-open), a style's
    // button that brings the list up opens it
    new MutationObserver(() => {
      const cl = B().classList, open = cl.contains('card-open');
      if (open && !card && (S.rail || S.find || S.side)) set({});
      card = open;
      if (c.phone && cl.contains('menu-open') !== S.find) set({ find: !S.find });
      fitSoon();
    }).observe(B(), { attributeFilter: ['class'] });
    // a press on the map closes what is open (the map is what the visitor wants to see)
    document.addEventListener('pointerdown', (e) => {
      if (c.phone && (S.rail || S.find || S.side) && !e.target.closest?.('#ph-rail,#ph-side,#ph-time,#explore,.wf-box,[role="dialog"]')) set({});
    });
    // Escape closes one level: the choices, then Settings
    addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !c.phone || !(S.rail || S.find || S.side)) return;
      e.stopImmediatePropagation();
      const was = S.rail && !S.row;
      set(S.row ? { rail: true } : {}, !!S.row);
      if (was) $('#ph-set')?.focus();
    }, true);
    api.tours.on('start', () => set({})); // VIS-251: a tour closes the menu
    api.tours.on('stop', (e) => { stop = e; drawTour(); });
    api.tours.on('end', () => { stop = null; drawTour(); });
  }

  return Object.assign(ph, {
    /** After every draw of the page: the rows for what is drawn, the tools, the pin and the credit in the column, what
     *  the column stands for hidden, the open choices drawn again from the page's controls. */
    sync() {
      cls('ph', c.phone); // (a boolean: phoneOn)
      cls('ph-wayfind', c.phone && c.has('wayfind'));
      if (!c.phone) return void ((S.rail || S.find || S.side) && set({}));
      bind();
      rows = railRows(drawn, ph.rows);
      const f = $('#ph-find');
      if (!f) return;
      f.hidden = !drawn('explore.panel') || drawn('wayfind');
      // a preset without the search field: the button says Explore (the list and the categories), not Search
      const q = String(drawn('explore.search'));
      if (f.dataset.q !== q) { f.dataset.q = q; f.innerHTML = html`${icon(q === 'true' ? 'search' : 'menu')}`; f.ariaLabel = f.title = T(q === 'true' ? 'ph.search' : 'explore.open'); }
      $('#ph-set').hidden = $('.ph-dv').hidden = !rows.length;
      adopt(); drawTour();
      if (S.find && f.hidden) set({}); else apply();
    },
    /** A value moved (the hour, the lights, the trees): the open column says it. */
    tick() { if (c.phone && (S.rail || S.side)) paint(); },
  });
}
